import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './GradientTypography.module.scss';

export interface GradientTypographyProps {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  title: string;
  className?: string;
  gradient: string;
}

export const GradientTypography: React.FC<GradientTypographyProps> = ({
  as = 'h2',
  title,
  className = '',
  gradient,
}) => {
  const HeadingTag = as;
  const containerRef = useRef<HTMLHeadingElement>(null);

  const [lines, setLines] = useState<string[]>([]);

  const measure = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const computed = window.getComputedStyle(container);

    // Create hidden div to measure lines
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = `${container.clientWidth}px`;
    tempDiv.style.fontSize = 'var(--marm-typography-font-size-plus-xl, 38.4px)';
    tempDiv.style.fontWeight = computed.fontWeight;
    tempDiv.style.textTransform = 'uppercase';
    tempDiv.style.lineHeight = computed.lineHeight;
    tempDiv.style.letterSpacing = computed.letterSpacing;
    tempDiv.style.whiteSpace = 'normal';
    tempDiv.style.wordBreak = 'break-word';

    document.body.appendChild(tempDiv);

    const resultLines: string[] = [];
    const maxWidth = container.clientWidth - 16; // Account for padding

    const spanItem = document.createElement('span');
    spanItem.style.visibility = 'hidden';
    spanItem.style.position = 'absolute';
    spanItem.style.fontSize = 'var(--marm-typography-font-size-plus-xl, 38.4px)';
    spanItem.style.fontWeight = computed.fontWeight;
    spanItem.style.letterSpacing = computed.letterSpacing;
    spanItem.style.textTransform = computed.textTransform;
    spanItem.style.padding = computed.padding;
    tempDiv.appendChild(spanItem);

    // Split into words and spaces (preserve spaces)
    const words = title.split(/(\s+)/);

    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine + word;
      spanItem.textContent = testLine;
      const testLineWidth = spanItem.getBoundingClientRect().width;

      if (testLineWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        // Word doesn't fit
        if (currentLine.trim()) {
          resultLines.push(currentLine);
        }

        // Check if word alone is wider than line (needs breaking mid-word)
        spanItem.textContent = word;
        const wordWidth = spanItem.getBoundingClientRect().width;

        if (wordWidth > maxWidth) {
          // Break inside the word
          let subWord = '';
          for (const char of word) {
            const testSub = subWord + char;
            spanItem.textContent = testSub;
            const subWidth = spanItem.getBoundingClientRect().width;
            if (subWidth > maxWidth && subWord) {
              resultLines.push(subWord);
              subWord = char;
            } else {
              subWord = testSub;
            }
          }
          if (subWord) {
            currentLine = subWord;
          } else {
            currentLine = '';
          }
        } else {
          currentLine = word;
        }
      }
    }

    if (currentLine.trim()) {
      resultLines.push(currentLine);
    }

    document.body.removeChild(tempDiv);
    setLines(resultLines);
  }, [title]);

  useEffect(() => {
    // Check if window is available (for SSR compatibility)
    if (typeof window !== 'undefined') {
      const handleResize = () => measure();

      const observer = new ResizeObserver(handleResize);
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure);
      } else {
        setTimeout(measure, 100);
      }

      window.addEventListener('resize', handleResize);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [measure]);

  return (
    <HeadingTag className={classNames(styles['title'], className)} ref={containerRef}>
      {/* Render full text for SEO when no lines are measured yet */}
      {lines.length === 0 ? (
        <span className={styles['seo-text']}></span>
      ) : (
        // Render gradient lines once measured
        lines.map((line, index) => {
          const isEven = index % 2 === 0;

          return (
            <span
              key={index}
              className={classNames(
                styles['line'],
                styles[isEven ? 'even' : 'odd'],
                styles[`gradient-${gradient}`],
              )}
            >
              {line}
            </span>
          );
        })
      )}
    </HeadingTag>
  );
};
